import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import DoctorIcon from '../../assets/svgs/DoctorIcon';
import EditIcon from '../../assets/svgs/EditIcon';
import ExamIcon from '../../assets/svgs/ExamIcon';
import PinIcon from '../../assets/svgs/PinIcon';
import RegistrationIcon from '../../assets/svgs/RegistrationIcon';

import translate from '../../libs/i18n';
import getInitialLetters from '../../utils/getInitialLetters';
import roundToPrecision from '../../utils/roundToPrecision';
import toTitleCase from '../../utils/toTitleCase';

import { InstitutionSummaryStyle } from './InstitutionSummaryStyle';

const InstitutionSummary = ({
  institutionSummaryData,
  examsCount,
  handleOpenEditHeadquarterForm,
}) => {
  const language = useSelector((state) => state.settings.language);

  const [count, setCount] = useState(0);
  const [timeCourse, setTimeCourse] = useState('month');

  const currentMaximum = useMemo(() => {
    if (examsCount && examsCount.maximum) {
      return examsCount.maximum;
    }

    return 0;
  }, [examsCount]);

  const currentCount = useMemo(() => {
    if (examsCount && timeCourse && timeCourse.length) {
      if (timeCourse === 'all') {
        return examsCount.all;
      }

      if (timeCourse === 'month') {
        return examsCount.month;
      }

      if (timeCourse === 'today') {
        return examsCount.today;
      }
    }

    return 0;
  }, [examsCount, timeCourse]);

  const currentPercent = useMemo(() => {
    if (currentCount && currentMaximum && currentMaximum > 0) {
      return roundToPrecision((currentCount / currentMaximum) * 100, 2);
    }

    return 0;
  }, [currentCount, currentMaximum]);

  useEffect(() => {
    if (currentCount < 0 || count === currentCount) {
      return null;
    }

    const timeout = setTimeout(() => {
      if (count < currentCount) {
        setCount(count + 1);
      } else {
        setCount(count - 1);
      }
    }, Math.abs(currentCount - count) / 500);

    return () => clearTimeout(timeout);
  }, [count, currentCount]);

  return (
    <InstitutionSummaryStyle>
      <div className="institution-summary-content">
        <button
          className="edit-headquarter-button"
          type="button"
          onClick={handleOpenEditHeadquarterForm}
        >
          <span className="icon">
            <EditIcon />
          </span>
          {translate('edit', language)}
        </button>
        <div className="institution-avatar-wrapper">
          {institutionSummaryData?.image ? (
            <div className="institution-avatar">
              <img
                className="institution-image"
                src={institutionSummaryData.image}
                alt={
                  institutionSummaryData?.name?.length
                    ? institutionSummaryData?.name
                    : 'avatar'
                }
              />
            </div>
          ) : (
            <div className="institution-avatar text-avatar">
              {getInitialLetters(institutionSummaryData?.name || 'I')}
            </div>
          )}
        </div>
        <div className="institution-information-content">
          <div className="institution-name-registration-wrapper">
            <h3 className="institution-name">{institutionSummaryData.name}</h3>
            {institutionSummaryData?.cnpj ? (
              <p className="institution-registration">
                {`${String(institutionSummaryData.cnpj).substring(
                  0,
                  2
                )}.${String(institutionSummaryData.cnpj).substring(
                  2,
                  5
                )}.${String(institutionSummaryData.cnpj).substring(
                  5,
                  8
                )}/${String(institutionSummaryData.cnpj).substring(
                  8,
                  12
                )}-${String(institutionSummaryData.cnpj).substring(12, 14)}`}
              </p>
            ) : null}
          </div>

          <p className="institution-address">
            {institutionSummaryData?.address ? (
              <>
                <span className="icon">
                  <PinIcon />
                </span>
                {`${
                  institutionSummaryData.address
                    ? `${institutionSummaryData.address},`
                    : ''
                } ${institutionSummaryData.number} ${
                  institutionSummaryData.city
                    ? `- ${institutionSummaryData.city}`
                    : ''
                } ${
                  institutionSummaryData.country
                    ? `, ${institutionSummaryData.country}`
                    : ''
                } ${
                  institutionSummaryData.post_code
                    ? `, ${institutionSummaryData.post_code}`
                    : ''
                }`}
              </>
            ) : null}
          </p>

          <div className="institution-count-list">
            <div className="count-item registration-count">
              <span className="icon">
                <RegistrationIcon />
              </span>
              {`${
                examsCount?.registrations ? examsCount?.registrations : 0
              } ${translate('registrations', language).toLowerCase()}`}
            </div>
            <div className="count-item">
              <span className="icon">
                <DoctorIcon />
              </span>
              {institutionSummaryData?.pathology_name
                ? institutionSummaryData?.pathology_name
                : null}
            </div>
            <div className="count-item">
              <span className="icon">
                <ExamIcon />
              </span>
              {`${examsCount?.maximum ? examsCount?.maximum : 0} ${translate(
                'examsPerMonth',
                language
              )}`}
            </div>
          </div>
        </div>
      </div>
      <div className="institution-summary-exam-count">
        <div className="time-course-control">
          <button
            className={`time-course-button${
              timeCourse === 'month' ? ' active' : ''
            }`}
            type="button"
            disabled={timeCourse === 'month'}
            onClick={() => setTimeCourse('month')}
          >
            {translate('thisMonth', language)}
          </button>
          <button
            className={`time-course-button${
              timeCourse === 'today' ? ' active' : ''
            }`}
            type="button"
            disabled={timeCourse === 'today'}
            onClick={() => setTimeCourse('today')}
          >
            {translate('today', language)}
          </button>
          <button
            className={`time-course-button${
              timeCourse === 'all' ? ' active' : ''
            }`}
            type="button"
            disabled={timeCourse === 'all'}
            onClick={() => setTimeCourse('all')}
          >
            {translate('all', language)}
          </button>
        </div>
        <div className="summary-exam-count-information">
          <span className="exam-count">{`${count} `}</span>
          <span className="exam-count-total">/ {`${currentMaximum} `}</span>
          <span className="exam-count-label">
            {toTitleCase(translate('exams', language))}
          </span>
        </div>
        <div className="exam-count-progress-bar-container">
          <div
            className="exam-count-progress-value"
            style={{ width: `${currentPercent}%` }}
          />
        </div>
      </div>
    </InstitutionSummaryStyle>
  );
};

InstitutionSummary.propTypes = {
  examsCount: PropTypes.shape({
    all: PropTypes.number,
    maximum: PropTypes.number,
    month: PropTypes.number,
    registrations: PropTypes.number,
    today: PropTypes.number,
  }),
  institutionSummaryData: PropTypes.shape({
    address: PropTypes.string,
    city: PropTypes.string,
    cnpj: PropTypes.string,
    country: PropTypes.string,
    image: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.string]),
    name: PropTypes.string,
    number: PropTypes.string,
    pathology_name: PropTypes.string,
    post_code: PropTypes.string,
  }),
  handleOpenEditHeadquarterForm: PropTypes.func.isRequired,
};

InstitutionSummary.defaultProps = {
  examsCount: {
    all: 0,
    maximum: 0,
    month: 0,
    registrations: 0,
    today: 0,
  },
  institutionSummaryData: {
    address: '',
    city: '',
    cnpj: '',
    country: '',
    image: null,
    name: '',
    number: '',
    pathology_name: '',
    post_code: '',
  },
};

export default InstitutionSummary;
